import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import mermaid from 'mermaid';

// Initialize mermaid once at module level to avoid repeated init calls
mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  themeVariables: {
    primaryColor: '#e0e7ff',
    primaryTextColor: '#312e81',
    primaryBorderColor: '#6366f1',
    lineColor: '#6366f1',
    secondaryColor: '#f0fdf4',
    tertiaryColor: '#fef3c7',
    fontSize: '14px'
  },
  flowchart: { useMaxWidth: true, htmlLabels: true, curve: 'basis' },
  securityLevel: 'loose'
});

// Serialize mermaid.render() calls to prevent concurrent rendering failures
let mermaidRenderQueue = Promise.resolve();
function queuedMermaidRender(id, text) {
  return new Promise((resolve, reject) => {
    mermaidRenderQueue = mermaidRenderQueue.then(async () => {
      try {
        const result = await mermaid.render(id, text);
        resolve(result);
      } catch (err) {
        reject(err);
      }
    });
  });
}

// Fix common LLM mermaid syntax mistakes
function sanitizeMermaid(text) {
  let s = text;
  // Fix -->|label|> to -->|label| (trailing > after pipe labels is invalid)
  s = s.replace(/\|>/g, '|');
  // Fix arrow-inline decision nodes: -->{Text} NodeId to --> NodeId{Text}
  s = s.replace(/(-->|---)\s*\{([^}]+)\}\s*([A-Za-z_]\w*)/g, '$1 $3{$2}');
  // Fix standalone {Text} used as source node without an ID — give it a generated ID
  let decCounter = 0;
  s = s.replace(/^(\s*)\{([^}]+)\}\s*(-->|---)/gm, (match, indent, label, arrow) => {
    decCounter++;
    return `${indent}dec${decCounter}{${label}} ${arrow}`;
  });
  // Fix special chars in labels that break mermaid: remove quotes, semicolons, and node-syntax chars inside pipes/brackets
  s = s.replace(/\|([^|]*)\|/g, (match, label) => '|' + label.replace(/[";{}()[\]]/g, '') + '|');
  s = s.replace(/\[([^\]]*)\]/g, (match, label) => '[' + label.replace(/[";{}()]/g, '') + ']');
  // Remove question marks from decision diamond labels (can break mermaid)
  s = s.replace(/\{([^}]*)\?([^}]*)\}/g, '{$1$2}');
  return s;
}

function LawDiagramRenderer({ diagramData }) {
  const containerRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState(null);

  const diagram = diagramData?.mermaidDiagram || diagramData;
  const description = diagramData?.description || '';
  const title = diagramData?.title || 'Diagram';

  useEffect(() => {
    if (!diagram) return;

    const renderDiagram = async () => {
      let diagramText = typeof diagram === 'string' ? diagram.replace(/\\n/g, '\n') : '';
      diagramText = sanitizeMermaid(diagramText);
      try {
        const id = 'law-diagram-' + Math.random().toString(36).substr(2, 9);
        const { svg } = await queuedMermaidRender(id, diagramText);
        setSvgContent(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err, '\nDiagram text:', diagramText);
        setError('Could not render diagram');
      }
    };

    renderDiagram();
  }, [diagram]);

  if (!diagram) return null;

  return (
    <div className="mb-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-2">{title}</h4>
      {description && (
        <p className="text-sm text-gray-600 mb-3">{description}</p>
      )}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      ) : svgContent ? (
        <div
          ref={containerRef}
          className="overflow-x-auto bg-white border border-gray-200 rounded-lg p-4 flex justify-center"
          dangerouslySetInnerHTML={{ __html: svgContent }}
        />
      ) : (
        <div className="flex items-center justify-center p-8 text-gray-400">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500 mr-3"></div>
          Rendering diagram...
        </div>
      )}
    </div>
  );
}

function ProcessDiagramAccordion({ diagramData }) {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState(null);

  const diagram = diagramData?.mermaidDiagram || diagramData;
  const description = diagramData?.description || '';
  const title = diagramData?.title || 'Process Diagram';

  useEffect(() => {
    if (!diagram) return;

    const renderDiagram = async () => {
      let diagramText = typeof diagram === 'string' ? diagram.replace(/\\n/g, '\n') : '';
      diagramText = sanitizeMermaid(diagramText);
      try {
        const id = 'law-process-' + Math.random().toString(36).substr(2, 9);
        const { svg } = await queuedMermaidRender(id, diagramText);
        setSvgContent(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err, '\nDiagram text:', diagramText);
        setError('Could not render diagram');
      }
    };

    renderDiagram();
  }, [diagram]);

  if (!diagram) return null;

  return (
    <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
      {/* Collapsed: header with thumbnail on right */}
      {!expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full flex items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        >
          <div className="flex-1 min-w-0 mr-4">
            <h4 className="text-base font-semibold text-gray-900">{title}</h4>
            {description && (
              <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{description}</p>
            )}
            <span className="text-xs text-indigo-600 font-medium mt-1 inline-block">Click to expand</span>
          </div>
          {svgContent && (
            <div
              className="flex-shrink-0 overflow-hidden rounded border border-gray-200 bg-white relative"
              style={{ width: '160px', height: '100px' }}
            >
              <div
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
                style={{ transform: 'scale(0.25)', transformOrigin: 'center center' }}
              >
                <div dangerouslySetInnerHTML={{ __html: svgContent }} />
              </div>
            </div>
          )}
          {!svgContent && !error && (
            <div className="flex-shrink-0 w-[160px] h-[100px] rounded border border-gray-200 bg-white flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-400"></div>
            </div>
          )}
          <svg
            className="w-5 h-5 text-gray-400 flex-shrink-0 ml-3"
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {/* Error */}
      {error && !expanded && (
        <div className="px-4 py-2 bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {/* Expanded: full diagram */}
      {expanded && (
        <div>
          <button
            onClick={() => setExpanded(false)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
          >
            <div className="flex-1 min-w-0">
              <h4 className="text-base font-semibold text-gray-900">{title}</h4>
              {description && (
                <p className="text-sm text-gray-500 mt-0.5">{description}</p>
              )}
            </div>
            <svg
              className="w-5 h-5 text-gray-400 flex-shrink-0 ml-3 rotate-180"
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {error ? (
            <div className="px-4 py-3 bg-red-50 text-red-700 text-sm">{error}</div>
          ) : svgContent ? (
            <div
              ref={containerRef}
              className="overflow-x-auto p-4 flex justify-center"
              dangerouslySetInnerHTML={{ __html: svgContent }}
            />
          ) : (
            <div className="flex items-center justify-center p-8 text-gray-400">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500 mr-2"></div>
              Rendering...
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LawAnalysis() {
  const { user, isAdmin } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [inputMode, setInputMode] = useState('url');
  const [textContent, setTextContent] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState('create');
  const [folderModalName, setFolderModalName] = useState('');
  const [folderModalTarget, setFolderModalTarget] = useState(null);
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [metadataForm, setMetadataForm] = useState({
    scope: '',
    jurisdiction: '',
    state_code: '',
    legislative_status: ''
  });

  // Debug: Log admin status
  useEffect(() => {
    console.log('LawAnalysis - isAdmin:', isAdmin, 'user:', user);
  }, [isAdmin, user]);

  // Load analysis if UUID in URL, clear state when returning to list
  useEffect(() => {
    if (id) {
      loadAnalysis(id);
    } else {
      setAnalysis(null);
      setResults([]);
      setLoading(false);
    }
    loadRecentAnalyses();
    if (user) fetchLawFolders();
  }, [id]);

  const fetchLawFolders = useCallback(async () => {
    try {
      const response = await fetch('/api/law-folders', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFolders(data.folders || []);
      }
    } catch (err) {
      console.error('Error fetching law folders:', err);
    }
  }, []);

  // Auto-populate metadata form when analysis data arrives
  useEffect(() => {
    if (analysis && (analysis.scope || analysis.jurisdiction || analysis.state_code || analysis.legislative_status)) {
      setMetadataForm({
        scope: analysis.scope || '',
        jurisdiction: analysis.jurisdiction || '',
        state_code: analysis.state_code || '',
        legislative_status: analysis.legislative_status || ''
      });
    }
  }, [analysis]);

  async function loadRecentAnalyses() {
    try {
      const response = await fetch('/api/law-analyses?limit=100');
      const data = await response.json();
      if (data.success) {
        setRecentAnalyses(data.analyses);
      }
    } catch (err) {
      console.error('Failed to load recent analyses:', err);
    }
  }

  // Filter analyses based on search query AND selected folder
  const filteredAnalyses = recentAnalyses.filter(item => {
    // Folder filter
    if (selectedFolder === 'unfiled') {
      if (item.folder_id) return false;
    } else if (selectedFolder) {
      const folder = folders.find(f => f.uuid === selectedFolder);
      if (!folder || item.folder_id !== folder.id) return false;
    }
    // Search filter
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.title?.toLowerCase().includes(query) ||
      item.url?.toLowerCase().includes(query) ||
      item.status?.toLowerCase().includes(query)
    );
  });

  async function handleCreateLawFolder() {
    if (!folderModalName.trim()) return;
    try {
      const response = await fetch('/api/law-folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: folderModalName.trim() })
      });
      if (response.ok) {
        setFolderModalOpen(false);
        setFolderModalName('');
        fetchLawFolders();
      }
    } catch (err) {
      alert('Error creating folder: ' + err.message);
    }
  }

  async function handleRenameLawFolder() {
    if (!folderModalName.trim() || !folderModalTarget) return;
    try {
      const response = await fetch(`/api/law-folders/${folderModalTarget}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: folderModalName.trim() })
      });
      if (response.ok) {
        setFolderModalOpen(false);
        setFolderModalName('');
        setFolderModalTarget(null);
        fetchLawFolders();
      }
    } catch (err) {
      alert('Error renaming folder: ' + err.message);
    }
  }

  async function handleDeleteLawFolder(uuid) {
    if (!window.confirm('Delete this folder? Laws will be moved to Unfiled.')) return;
    try {
      const response = await fetch(`/api/law-folders/${uuid}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        if (selectedFolder === uuid) setSelectedFolder(null);
        fetchLawFolders();
        loadRecentAnalyses();
      }
    } catch (err) {
      alert('Error deleting folder: ' + err.message);
    }
  }

  async function handleMoveLawToFolder(lawUuid, folderUuid) {
    try {
      const response = await fetch(`/api/law-analysis/${lawUuid}/folder`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ folderUuid })
      });
      if (response.ok) {
        loadRecentAnalyses();
        fetchLawFolders();
      }
    } catch (err) {
      alert('Error moving law: ' + err.message);
    }
  }

  async function loadAnalysis(uuid) {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/law-analysis/${uuid}`);
      const data = await response.json();

      if (data.success) {
        setAnalysis(data.analysis);
        setResults(data.results);

        // If still processing, keep loading true and start polling
        if (data.analysis.status === 'processing') {
          startPolling(uuid);
          // Don't set loading to false - let polling handle it
        } else {
          // Analysis is completed or failed, stop loading
          setLoading(false);
        }
      } else {
        setError(data.error || 'Failed to load analysis');
        setLoading(false);
      }
    } catch (err) {
      setError('Error loading analysis: ' + err.message);
      setLoading(false);
    }
  }

  const isSubmitDisabled = loading || (inputMode === 'url' && !url.trim()) || (inputMode === 'text' && !textContent.trim()) || (inputMode === 'file' && !file);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    setAnalysis(null);
    setResults([]);

    try {
      let response;

      if (inputMode === 'file') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('private', isPrivate);
        response = await fetch('/api/law-analysis', {
          method: 'POST',
          body: formData
        });
      } else {
        const payload = { private: isPrivate };
        if (inputMode === 'text') {
          payload.text = textContent;
        } else {
          payload.url = url;
        }
        response = await fetch('/api/law-analysis', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      const data = await response.json();

      if (data.success) {
        navigate(`/law-analysis/${data.uuid}`);
        startPolling(data.uuid);
        loadRecentAnalyses();
      } else {
        setError(data.error || 'Analysis failed');
        setLoading(false);
      }
    } catch (err) {
      setError('Error submitting analysis: ' + err.message);
      setLoading(false);
    }
  }

  function startPolling(uuid) {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`/api/law-analysis/${uuid}`);
        const data = await response.json();

        if (data.success && data.analysis) {
          setAnalysis(data.analysis);

          if (data.analysis.status === 'completed') {
            // Analysis complete, load results
            setResults(data.results || []);
            setLoading(false);
            clearInterval(pollInterval);
          } else if (data.analysis.status === 'failed') {
            // Analysis failed
            setError('Analysis failed. Please try again.');
            setLoading(false);
            clearInterval(pollInterval);
          }
          // If status is 'processing', keep polling
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000); // Poll every 3 seconds

    // Stop polling after 10 minutes max
    setTimeout(() => {
      clearInterval(pollInterval);
      if (loading) {
        setError('Analysis timed out. Please try again.');
        setLoading(false);
      }
    }, 600000);
  }

  async function handleDelete() {
    if (!window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/law-analysis/${analysis.uuid}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        // Clear current analysis and redirect to main page
        setAnalysis(null);
        setResults([]);
        navigate('/law-analysis');
        loadRecentAnalyses();
      } else {
        alert('Error deleting analysis: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error deleting analysis: ' + err.message);
    }
  }

  async function handleDeleteFromList(uuid, e) {
    e.stopPropagation(); // Prevent navigation to detail view

    if (!window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/law-analysis/${uuid}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success) {
        // If deleted item was currently selected, clear it
        if (analysis?.uuid === uuid) {
          setAnalysis(null);
          setResults([]);
          navigate('/law-analysis');
        }
        loadRecentAnalyses();
      } else {
        alert('Error deleting analysis: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error deleting analysis: ' + err.message);
    }
  }

  async function handleReanalyze() {
    if (!analysis?.uuid) return;

    setError('');
    setLoading(true);
    setResults([]);

    try {
      const response = await fetch(`/api/law-analysis/${analysis.uuid}/reanalyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(response.status === 401 ? 'Please log in to re-analyze.' : `Server error: ${errText}`);
      }

      const data = await response.json();

      if (data.success) {
        // Update analysis status to processing with initial message
        setAnalysis({
          ...analysis,
          status: 'processing',
          progress: 0,
          progress_message: 'Starting re-analysis...'
        });
        // Start polling for results with the same UUID
        startPolling(data.uuid);
      } else {
        setError(data.error || 'Re-analysis failed');
        setLoading(false);
      }
    } catch (err) {
      setError('Error starting re-analysis: ' + err.message);
      setLoading(false);
    }
  }

  function handleEditMetadata() {
    // Populate form with current values
    setMetadataForm({
      scope: analysis.scope || '',
      jurisdiction: analysis.jurisdiction || '',
      state_code: analysis.state_code || '',
      legislative_status: analysis.legislative_status || ''
    });
    setIsEditingMetadata(true);
  }

  function handleCancelEdit() {
    setIsEditingMetadata(false);
  }

  async function handleSaveMetadata(e) {
    e.preventDefault();

    try {
      const response = await fetch(`/api/law-analysis/${analysis.uuid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(metadataForm)
      });

      const data = await response.json();

      if (data.success) {
        setAnalysis(data.analysis);
        // Only close the form if analysis is completed, keep it open during processing
        if (isEditingMetadata && data.analysis.status === 'completed') {
          setIsEditingMetadata(false);
        }
        loadRecentAnalyses(); // Refresh the list
        // Show success message
        if (data.analysis.status === 'processing') {
          alert('Metadata updated successfully!');
        }
      } else {
        alert('Error updating metadata: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error updating metadata: ' + err.message);
    }
  }

  function renderVisualization(result) {
    const { graphical_data, dimension_name, visualization_type, categories } = result;

    if (!graphical_data || graphical_data.length === 0) {
      return <p className="text-gray-500 italic">No visualization data available</p>;
    }

    // Parse categories if string
    const cats = typeof categories === 'string' ? JSON.parse(categories) : categories;

    if (visualization_type === 'spectrum') {
      return renderSpectrum(graphical_data, cats);
    } else if (visualization_type === 'impact-chart') {
      return renderImpactChart(graphical_data, cats);
    } else if (visualization_type === 'funding-breakdown') {
      return renderFundingBreakdown(graphical_data, cats);
    } else if (visualization_type === 'risk-matrix') {
      return renderRiskMatrix(graphical_data, cats);
    } else if (visualization_type === 'stakeholder-risks') {
      return renderStakeholderRisks(graphical_data, cats);
    }

    return <p className="text-gray-500 italic">Unknown visualization type</p>;
  }

  function renderSpectrum(data, categories) {
    // Check if this is values lens with numeric scores (two-pass returns x_score/y_score)
    const hasNumericScores = data.length > 0 && (typeof data[0].score === 'number' || typeof data[0].x_score === 'number');

    if (hasNumericScores) {
      // Render scatter plot for ideological position vs impact
      return <IdeologicalScatterPlot data={data} />;
    }

    // Fallback: render grouped by category
    return (
      <div className="space-y-2">
        {categories.map(cat => {
          const provisions = data.filter(d => d.category === cat.value);
          if (provisions.length === 0) return null;

          return (
            <div key={cat.value} className="border-l-4 border-primary-500 pl-4 py-2">
              <h5 className="font-semibold text-gray-900">{cat.label}</h5>
              <p className="text-xs text-gray-600 mt-1">{cat.description}</p>
              <ul className="mt-2 space-y-2">
                {provisions.map((prov, idx) => (
                  <li key={idx} className="text-sm text-gray-700 bg-gray-50 rounded p-2">
                    <div className="flex items-start">
                      <span className="font-medium mr-2">•</span>
                      <div className="flex-1">
                        <p className="font-medium">{prov.fragment || prov.provision}</p>
                        {prov.explanation && (
                          <p className="text-xs text-gray-600 mt-1">{prov.explanation}</p>
                        )}
                        {prov.impact_level && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-primary-100 text-primary-800 rounded">
                            Impact: {prov.impact_level}/10
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  function IdeologicalSourceModal({ point, onClose }) {
    // 2D color: blend based on quadrant
    const getQuadrantInfo = (x, y) => {
      if (x <= 0 && y <= 0) return { label: 'Individual Freedom', color: '#2563eb', desc: 'Prioritizes personal liberty and individual autonomy' };
      if (x > 0 && y <= 0) return { label: 'Community Freedom', color: '#16a34a', desc: 'Expands collective rights while reducing restrictions' };
      if (x <= 0 && y > 0) return { label: 'Individual Control', color: '#d97706', desc: 'Regulates individual behavior through mandates' };
      return { label: 'Community Control', color: '#dc2626', desc: 'Collective regulation with enforcement mechanisms' };
    };

    const xs = point.x_score ?? point.score ?? 0;
    const ys = point.y_score ?? 0;
    const qi = getQuadrantInfo(xs, ys);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="fixed inset-0 bg-black bg-opacity-50"></div>
        <div
          className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: qi.color }}></div>
                <span className="text-sm font-bold" style={{ color: qi.color }}>{qi.label}</span>
                <span className="text-sm text-gray-500">
                  Individual↔Community: {xs} | Freedom↔Control: {ys} | Impact: {point.impact_level}/10
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900">{point.fragment}</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-6 py-4">
            {point.explanation && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">Moral & Societal Analysis</h4>
                <p className="text-gray-700">{point.explanation}</p>
              </div>
            )}
            {point.moral_foundations && point.moral_foundations.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Moral Foundations</h4>
                <div className="flex flex-wrap gap-2">
                  {point.moral_foundations.map((f, i) => (
                    <span key={i} className="px-3 py-1 rounded-full text-xs font-semibold" style={{
                      background: f === 'Care' ? '#dbeafe' : f === 'Fairness' ? '#e0e7ff' : f === 'Liberty' ? '#fef3c7' :
                        f === 'Loyalty' ? '#dcfce7' : f === 'Authority' ? '#fce7f3' : f === 'Sanctity' ? '#f3e8ff' : '#f3f4f6',
                      color: f === 'Care' ? '#1e40af' : f === 'Fairness' ? '#3730a3' : f === 'Liberty' ? '#92400e' :
                        f === 'Loyalty' ? '#166534' : f === 'Authority' ? '#9d174d' : f === 'Sanctity' ? '#6b21a8' : '#374151'
                    }}>{f}</span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Source Text from the Law</h4>
              {point.source_text ? (
                <blockquote className="border-l-4 border-gray-300 pl-4 py-3 bg-gray-50 rounded-r-lg text-gray-800 italic">
                  "{point.source_text}"
                </blockquote>
              ) : (
                <p className="text-gray-500 italic">No specific source text available. Re-analyze to generate source references.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function IdeologicalScatterPlot({ data }) {
    const [selectedPoint, setSelectedPoint] = React.useState(null);
    const [modalPoint, setModalPoint] = React.useState(null);

    // Chart dimensions
    const width = 700;
    const height = 700;
    const padding = 70;
    const chartWidth = width - (2 * padding);
    const chartHeight = height - (2 * padding);

    // Scale functions — 2D: X = Community(left)↔Individual(right), Y = Freedom(top)↔Control(bottom)
    const scaleX = (xScore) => {
      // x_score: -10 (Individual) to +10 (Community), but display flipped: Community left, Individual right
      return padding + ((-xScore + 10) / 20) * chartWidth;
    };

    const scaleY = (yScore) => {
      // y_score: -10 (Freedom, top) to +10 (Control, bottom)
      return padding + ((yScore + 10) / 20) * chartHeight;
    };

    // Get color based on quadrant position
    const getColor = (xScore, yScore) => {
      const x = xScore ?? 0;
      const y = yScore ?? 0;
      // Quadrant colors with intensity based on distance from center
      const dist = Math.sqrt(x * x + y * y) / 14.14; // normalize to 0-1
      const alpha = 0.5 + dist * 0.5;
      if (x <= 0 && y <= 0) return `rgba(37, 99, 235, ${alpha})`; // Individual+Freedom = blue
      if (x > 0 && y <= 0) return `rgba(22, 163, 74, ${alpha})`; // Community+Freedom = green
      if (x <= 0 && y > 0) return `rgba(217, 119, 6, ${alpha})`; // Individual+Control = amber
      return `rgba(220, 38, 38, ${alpha})`; // Community+Control = red
    };

    const getQuadrantLabel = (x, y) => {
      if (x <= 0 && y <= 0) return 'Individual Freedom';
      if (x > 0 && y <= 0) return 'Community Freedom';
      if (x <= 0 && y > 0) return 'Individual Control';
      return 'Community Control';
    };

    // Support both old format (score) and new format (x_score, y_score)
    const getXScore = (p) => p.x_score ?? p.score ?? 0;
    const getYScore = (p) => p.y_score ?? 0;

    return (
      <div className="space-y-4">
        {/* 2D Scatter plot */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 overflow-x-auto">
          <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" style={{ maxWidth: `${width}px`, minWidth: '320px' }}>
            {/* Quadrant backgrounds: left=Community, right=Individual, top=Freedom, bottom=Control */}
            <rect x={padding} y={padding} width={chartWidth / 2} height={chartHeight / 2}
              fill="rgba(22, 163, 74, 0.04)" />
            <rect x={padding + chartWidth / 2} y={padding} width={chartWidth / 2} height={chartHeight / 2}
              fill="rgba(37, 99, 235, 0.04)" />
            <rect x={padding} y={padding + chartHeight / 2} width={chartWidth / 2} height={chartHeight / 2}
              fill="rgba(220, 38, 38, 0.04)" />
            <rect x={padding + chartWidth / 2} y={padding + chartHeight / 2} width={chartWidth / 2} height={chartHeight / 2}
              fill="rgba(217, 119, 6, 0.04)" />

            {/* Quadrant labels */}
            <text x={padding + chartWidth * 0.25} y={padding + 20} textAnchor="middle" fontSize="11" fill="#16a34a" fontWeight="600" opacity="0.6">Community Freedom</text>
            <text x={padding + chartWidth * 0.75} y={padding + 20} textAnchor="middle" fontSize="11" fill="#2563eb" fontWeight="600" opacity="0.6">Individual Freedom</text>
            <text x={padding + chartWidth * 0.25} y={padding + chartHeight - 10} textAnchor="middle" fontSize="11" fill="#dc2626" fontWeight="600" opacity="0.6">Community Control</text>
            <text x={padding + chartWidth * 0.75} y={padding + chartHeight - 10} textAnchor="middle" fontSize="11" fill="#d97706" fontWeight="600" opacity="0.6">Individual Control</text>

            {/* Border */}
            <rect x={padding} y={padding} width={chartWidth} height={chartHeight}
              fill="none" stroke="#e5e7eb" strokeWidth="2" />

            {/* Center crosshair */}
            <line x1={scaleX(0)} y1={padding} x2={scaleX(0)} y2={height - padding}
              stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="4,4" />
            <line x1={padding} y1={scaleY(0)} x2={width - padding} y2={scaleY(0)}
              stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="4,4" />

            {/* Grid lines */}
            {[-5, 5].map(v => (
              <React.Fragment key={`grid-${v}`}>
                <line x1={scaleX(v)} y1={padding} x2={scaleX(v)} y2={height - padding}
                  stroke="#f3f4f6" strokeWidth="1" strokeDasharray="2,4" />
                <line x1={padding} y1={scaleY(v)} x2={width - padding} y2={scaleY(v)}
                  stroke="#f3f4f6" strokeWidth="1" strokeDasharray="2,4" />
              </React.Fragment>
            ))}

            {/* X-axis labels */}
            <text x={padding} y={height - padding + 35} textAnchor="middle" fontSize="11" fill="#666">10</text>
            <text x={scaleX(5)} y={height - padding + 35} textAnchor="middle" fontSize="11" fill="#666">5</text>
            <text x={scaleX(0)} y={height - padding + 35} textAnchor="middle" fontSize="11" fill="#666">0</text>
            <text x={scaleX(-5)} y={height - padding + 35} textAnchor="middle" fontSize="11" fill="#666">-5</text>
            <text x={width - padding} y={height - padding + 35} textAnchor="middle" fontSize="11" fill="#666">-10</text>
            <text x={padding + 10} y={height - padding + 55} textAnchor="start" fontSize="13" fill="#1a237e" fontWeight="700">
              Community
            </text>
            <text x={width / 2} y={height - padding + 55} textAnchor="middle" fontSize="12" fill="#999">
              ← Who is prioritized? →
            </text>
            <text x={width - padding - 10} y={height - padding + 55} textAnchor="end" fontSize="13" fill="#1a237e" fontWeight="700">
              Individual
            </text>

            {/* Y-axis labels */}
            <text x={padding - 15} y={padding} textAnchor="middle" fontSize="11" fill="#666">-10</text>
            <text x={padding - 15} y={scaleY(-5)} textAnchor="middle" fontSize="11" fill="#666">-5</text>
            <text x={padding - 15} y={scaleY(0)} textAnchor="middle" fontSize="11" fill="#666">0</text>
            <text x={padding - 15} y={scaleY(5)} textAnchor="middle" fontSize="11" fill="#666">5</text>
            <text x={padding - 15} y={height - padding} textAnchor="middle" fontSize="11" fill="#666">10</text>
            <text x={20} y={padding + 10} textAnchor="middle" fontSize="13" fill="#1a237e" fontWeight="700"
              transform={`rotate(-90 20 ${padding + 10})`}>Freedom</text>
            <text x={20} y={height / 2} textAnchor="middle" fontSize="12" fill="#999"
              transform={`rotate(-90 20 ${height / 2})`}>← Governance style →</text>
            <text x={20} y={height - padding - 10} textAnchor="middle" fontSize="13" fill="#1a237e" fontWeight="700"
              transform={`rotate(-90 20 ${height - padding - 10})`}>Control</text>

            {/* Data points — size by impact_level */}
            {data.map((point, idx) => {
              const xs = getXScore(point);
              const ys = getYScore(point);
              const impact = point.impact_level || 5;
              const r = 4 + (impact / 10) * 5; // radius 4-9 based on impact
              return (
                <circle
                  key={idx}
                  cx={scaleX(xs)}
                  cy={scaleY(ys)}
                  r={selectedPoint === idx ? r + 3 : r}
                  fill={getColor(xs, ys)}
                  stroke={selectedPoint === idx ? '#1a237e' : '#fff'}
                  strokeWidth={selectedPoint === idx ? 2.5 : 1}
                  className="cursor-pointer transition-all"
                  onClick={() => setModalPoint(idx)}
                  onMouseEnter={(e) => { e.target.style.filter = 'brightness(0.8)'; e.target.setAttribute('r', String(r + 2)); }}
                  onMouseLeave={(e) => { e.target.style.filter = 'none'; e.target.setAttribute('r', String(r)); }}
                >
                  <title>{point.fragment} (x:{xs}, y:{ys})</title>
                </circle>
              );
            })}
          </svg>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(37, 99, 235, 0.8)' }}></div>
            <span>Individual + Freedom</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(22, 163, 74, 0.8)' }}></div>
            <span>Community + Freedom</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(217, 119, 6, 0.8)' }}></div>
            <span>Individual + Control</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(220, 38, 38, 0.8)' }}></div>
            <span>Community + Control</span>
          </div>
          <div className="flex items-center gap-1 ml-2 text-gray-500">
            <span>Dot size = significance</span>
          </div>
        </div>

        {/* Modal */}
        {modalPoint !== null && data[modalPoint] && (
          <IdeologicalSourceModal
            point={data[modalPoint]}
            onClose={() => setModalPoint(null)}
          />
        )}

        {/* Selected point details */}
        {selectedPoint !== null && data[selectedPoint] && (() => {
          const sp = data[selectedPoint];
          const xs = getXScore(sp);
          const ys = getYScore(sp);
          const ql = getQuadrantLabel(xs, ys);
          return (
            <div
              className="bg-blue-50 border border-blue-200 rounded-lg p-4 cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => setModalPoint(selectedPoint)}
            >
              <div className="flex items-start gap-2">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0 mt-1"
                  style={{ backgroundColor: getColor(xs, ys) }}
                ></div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{ql}</span>
                      <span className="text-sm text-gray-600">
                        Ind↔Com: {xs} | Free↔Ctrl: {ys} | Impact: {sp.impact_level}/10
                      </span>
                    </div>
                    <span className="text-xs text-blue-600 font-medium">Click for source text</span>
                  </div>
                  <p className="font-medium text-gray-900 mb-1">{sp.fragment}</p>
                  {sp.explanation && <p className="text-sm text-gray-700">{sp.explanation}</p>}
                  {sp.moral_foundations && sp.moral_foundations.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {sp.moral_foundations.map((f, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">{f}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* All fragments list */}
        <div>
          <h5 className="font-semibold text-gray-900 mb-2">All Phrases ({data.length})</h5>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {[...data].sort((a, b) => (b.impact_level || 0) - (a.impact_level || 0)).map((point, idx) => {
              const xs = getXScore(point);
              const ys = getYScore(point);
              return (
                <div
                  key={idx}
                  className={`border-l-4 pl-3 py-2 cursor-pointer rounded ${
                    selectedPoint === idx ? 'bg-blue-50 border-blue-500' : 'bg-gray-50 border-gray-300'
                  }`}
                  style={{ borderLeftColor: getColor(xs, ys) }}
                  onClick={() => setSelectedPoint(selectedPoint === idx ? null : idx)}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold" style={{ color: getColor(xs, ys) }}>
                            {getQuadrantLabel(xs, ys)}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({xs}, {ys})
                          </span>
                          <span className="text-xs text-gray-600">
                            Impact: {point.impact_level}/10
                          </span>
                        </div>
                        {selectedPoint === idx && (
                          <button
                            className="text-xs text-blue-600 font-medium hover:text-blue-800"
                            onClick={(e) => { e.stopPropagation(); setModalPoint(idx); }}
                          >
                            View source
                          </button>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-900">{point.fragment}</p>
                      {selectedPoint === idx && point.explanation && (
                        <p className="text-xs text-gray-700 mt-1">{point.explanation}</p>
                      )}
                      {selectedPoint === idx && point.moral_foundations && point.moral_foundations.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {point.moral_foundations.map((f, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">{f}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  function ImpactSourceModal({ item, onClose }) {
    const isBenefit = item.category && item.category.includes('benefit');
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="fixed inset-0 bg-black bg-opacity-50"></div>
        <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex items-start justify-between">
            <div>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase mb-2 ${isBenefit ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {isBenefit ? 'Benefit' : 'Negative Impact'}
              </span>
              <h3 className="text-lg font-bold text-gray-900">{item.provision}</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-6 py-4">
            {item.explanation && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">Explanation</h4>
                <p className="text-gray-700">{item.explanation}</p>
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Source Text from the Law</h4>
              {item.source_text ? (
                <blockquote className="border-l-4 border-gray-300 pl-4 py-3 bg-gray-50 rounded-r-lg text-gray-800 italic">
                  "{item.source_text}"
                </blockquote>
              ) : (
                <p className="text-gray-500 italic">No specific source text available. Re-analyze to generate source references.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function ImpactChartView({ data }) {
    const [modalItem, setModalItem] = React.useState(null);
    const benefits = data.filter(d => d.category && d.category.includes('benefit'));
    const harms = data.filter(d => d.category && d.category.includes('harm'));

    return (
      <div>
        {modalItem && <ImpactSourceModal item={modalItem} onClose={() => setModalItem(null)} />}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h5 className="font-semibold text-green-700 mb-2">Beneficiaries</h5>
            <div className="space-y-2">
              {benefits.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-green-50 border border-green-200 rounded p-2 cursor-pointer hover:bg-green-100 transition-colors"
                  onClick={() => setModalItem(item)}
                >
                  <div className="flex items-start justify-between">
                    <p className="font-medium text-sm text-gray-900">{item.provision}</p>
                    <span className="text-xs text-green-600 font-medium ml-2 flex-shrink-0">View source</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{item.explanation}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h5 className="font-semibold text-red-700 mb-2">Negatively Impacted</h5>
            <div className="space-y-2">
              {harms.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-red-50 border border-red-200 rounded p-2 cursor-pointer hover:bg-red-100 transition-colors"
                  onClick={() => setModalItem(item)}
                >
                  <div className="flex items-start justify-between">
                    <p className="font-medium text-sm text-gray-900">{item.provision}</p>
                    <span className="text-xs text-red-600 font-medium ml-2 flex-shrink-0">View source</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{item.explanation}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderImpactChart(data) {
    return <ImpactChartView data={data} />;
  }

  function FundingSourceModal({ item, categoryLabel, onClose }) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="fixed inset-0 bg-black bg-opacity-50"></div>
        <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex items-start justify-between">
            <div>
              <span className="inline-block px-2 py-0.5 rounded text-xs font-bold uppercase mb-2 bg-blue-100 text-blue-800">
                {categoryLabel}
              </span>
              <h3 className="text-lg font-bold text-gray-900">{item.provision}</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-6 py-4">
            {item.explanation && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">Explanation</h4>
                <p className="text-gray-700">{item.explanation}</p>
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Source Text from the Law</h4>
              {item.source_text ? (
                <blockquote className="border-l-4 border-gray-300 pl-4 py-3 bg-gray-50 rounded-r-lg text-gray-800 italic">
                  "{item.source_text}"
                </blockquote>
              ) : (
                <p className="text-gray-500 italic">No specific source text available. Re-analyze to generate source references.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function FundingBreakdownView({ data, categories }) {
    const [modalItem, setModalItem] = React.useState(null);
    const [modalLabel, setModalLabel] = React.useState('');

    return (
      <div>
        {modalItem && <FundingSourceModal item={modalItem} categoryLabel={modalLabel} onClose={() => setModalItem(null)} />}
        <div className="space-y-2">
          {data.map((item, idx) => {
            const category = categories.find(c => c.value === item.category);
            const label = category?.label || item.category;
            return (
              <div
                key={idx}
                className="flex items-start border-l-4 border-blue-500 pl-4 py-2 cursor-pointer hover:bg-blue-50 transition-colors rounded-r"
                onClick={() => { setModalItem(item); setModalLabel(label); }}
              >
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h5 className="font-semibold text-gray-900">{label}</h5>
                    <span className="text-xs text-blue-600 font-medium">View source</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-1">{item.provision}</p>
                  <p className="text-xs text-gray-600 mt-1">{item.explanation}</p>
                </div>
                {item.impact_level && (
                  <div className="ml-4 flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-lg font-bold text-blue-700">{item.impact_level}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderFundingBreakdown(data, categories) {
    return <FundingBreakdownView data={data} categories={categories} />;
  }

  function renderRiskMatrix(data, categories) {
    // Group by risk level
    return (
      <div className="space-y-3">
        {categories.map(cat => {
          const risks = data.filter(d => d.category === cat.value);
          if (risks.length === 0) return null;

          const colorClasses = {
            'critical': 'bg-red-100 border-red-500 text-red-900',
            'high': 'bg-orange-100 border-orange-500 text-orange-900',
            'moderate': 'bg-yellow-100 border-yellow-500 text-yellow-900',
            'low': 'bg-blue-100 border-blue-500 text-blue-900',
            'negligible': 'bg-gray-100 border-gray-500 text-gray-900'
          };

          const colorClass = colorClasses[cat.value] || 'bg-gray-100 border-gray-500 text-gray-900';

          return (
            <div key={cat.value} className={`border-l-4 pl-4 py-2 ${colorClass}`}>
              <h5 className="font-semibold">{cat.label}</h5>
              <ul className="mt-2 space-y-2">
                {risks.map((risk, idx) => (
                  <li key={idx} className="text-sm">
                    <p className="font-medium">{risk.provision}</p>
                    <p className="text-xs mt-1 opacity-80">{risk.explanation}</p>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  function SourceTextModal({ item, type, onClose }) {
    // type is 'risk' or 'benefit'
    const isRisk = type === 'risk';
    const title = isRisk ? item.risk : item.benefit;
    const severity = isRisk ? item.severity : item.significance;
    const sourceText = item.source_text;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="fixed inset-0 bg-black bg-opacity-50"></div>
        <div
          className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex items-start justify-between">
            <div>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase mb-2 ${
                isRisk ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
              }`}>
                {severity} {type}
              </span>
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-6 py-4">
            {item.explanation && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">Explanation</h4>
                <p className="text-gray-700">{item.explanation}</p>
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Source Text from the Law</h4>
              {sourceText ? (
                <blockquote className="border-l-4 border-gray-300 pl-4 py-3 bg-gray-50 rounded-r-lg text-gray-800 italic">
                  "{sourceText}"
                </blockquote>
              ) : (
                <p className="text-gray-500 italic">No specific source text available for this item. Re-analyze to generate source references.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function StakeholderCard({ stakeholder, onItemClick }) {
    const [expanded, setExpanded] = React.useState(false);

    const impactColors = {
      'positive': 'bg-green-100 text-green-800 border-green-300',
      'negative': 'bg-red-100 text-red-800 border-red-300',
      'mixed': 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };

    const severityColors = {
      'critical': 'bg-red-100 border-red-400 text-red-900',
      'high': 'bg-orange-100 border-orange-400 text-orange-900',
      'moderate': 'bg-yellow-100 border-yellow-400 text-yellow-900',
      'low': 'bg-blue-100 border-blue-400 text-blue-900'
    };

    const significanceColors = {
      'major': 'bg-green-100 border-green-400 text-green-900',
      'moderate': 'bg-emerald-50 border-emerald-300 text-emerald-900',
      'minor': 'bg-teal-50 border-teal-300 text-teal-900'
    };

    const vulnLevel = stakeholder.vulnerability_level || 5;
    const vulnColor = vulnLevel >= 8 ? 'bg-red-500' : vulnLevel >= 5 ? 'bg-orange-500' : 'bg-green-500';
    const risks = stakeholder.risks || [];
    const benefits = stakeholder.benefits || [];

    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div
          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h5 className="font-bold text-gray-900 text-lg">{stakeholder.stakeholder}</h5>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${impactColors[stakeholder.overall_impact] || impactColors['mixed']}`}>
                {stakeholder.overall_impact === 'positive' ? 'Benefits' : stakeholder.overall_impact === 'negative' ? 'At Risk' : 'Mixed Impact'}
              </span>
            </div>
            <svg className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Vulnerability meter */}
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-600 w-28 flex-shrink-0">Vulnerability: {vulnLevel}/10</span>
            <div className="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-2.5 rounded-full transition-all ${vulnColor}`}
                style={{ width: `${vulnLevel * 10}%` }}
              ></div>
            </div>
          </div>

          {/* Summary counts */}
          <div className="flex gap-4 mt-2 text-xs text-gray-600">
            {risks.length > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-400"></span>
                {risks.length} risk{risks.length !== 1 ? 's' : ''}
              </span>
            )}
            {benefits.length > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-400"></span>
                {benefits.length} benefit{benefits.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="px-4 pb-4 border-t border-gray-100">
            {/* Risks */}
            {risks.length > 0 && (
              <div className="mt-4">
                <h6 className="font-semibold text-red-700 text-sm mb-2 uppercase tracking-wide">Risks</h6>
                <div className="space-y-2">
                  {risks.map((risk, idx) => (
                    <div
                      key={idx}
                      className={`border-l-4 pl-3 py-2 rounded-r cursor-pointer hover:opacity-80 transition-opacity ${severityColors[risk.severity] || severityColors['moderate']}`}
                      onClick={(e) => { e.stopPropagation(); onItemClick(risk, 'risk'); }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold uppercase">{risk.severity}</span>
                        <span className="text-xs text-gray-500">Click for source</span>
                      </div>
                      <p className="font-medium text-sm">{risk.risk}</p>
                      {risk.explanation && (
                        <p className="text-xs mt-1 opacity-80">{risk.explanation}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Benefits */}
            {benefits.length > 0 && (
              <div className="mt-4">
                <h6 className="font-semibold text-green-700 text-sm mb-2 uppercase tracking-wide">Benefits</h6>
                <div className="space-y-2">
                  {benefits.map((benefit, idx) => (
                    <div
                      key={idx}
                      className={`border-l-4 pl-3 py-2 rounded-r cursor-pointer hover:opacity-80 transition-opacity ${significanceColors[benefit.significance] || significanceColors['moderate']}`}
                      onClick={(e) => { e.stopPropagation(); onItemClick(benefit, 'benefit'); }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold uppercase">{benefit.significance}</span>
                        <span className="text-xs text-gray-500">Click for source</span>
                      </div>
                      <p className="font-medium text-sm">{benefit.benefit}</p>
                      {benefit.explanation && (
                        <p className="text-xs mt-1 opacity-80">{benefit.explanation}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {risks.length === 0 && benefits.length === 0 && (
              <p className="text-sm text-gray-500 italic mt-4">No specific risks or benefits identified for this group.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  function StakeholderRisksView({ data }) {
    const [modalItem, setModalItem] = React.useState(null);
    const [modalType, setModalType] = React.useState(null);

    if (!data || data.length === 0) {
      return <p className="text-gray-500 italic">No stakeholder data available</p>;
    }

    const handleItemClick = (item, type) => {
      setModalItem(item);
      setModalType(type);
    };

    // Sort by vulnerability level (highest first)
    const sorted = [...data].sort((a, b) => (b.vulnerability_level || 0) - (a.vulnerability_level || 0));

    // Separate into groups
    const atRisk = sorted.filter(s => s.overall_impact === 'negative');
    const mixed = sorted.filter(s => s.overall_impact === 'mixed');
    const benefiting = sorted.filter(s => s.overall_impact === 'positive');

    return (
      <div className="space-y-6">
        {/* Modal */}
        {modalItem && (
          <SourceTextModal
            item={modalItem}
            type={modalType}
            onClose={() => { setModalItem(null); setModalType(null); }}
          />
        )}

        {/* Overview bar */}
        <div className="flex gap-4 text-sm font-medium">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-400"></span>
            {atRisk.length} at risk
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-yellow-400"></span>
            {mixed.length} mixed
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-green-400"></span>
            {benefiting.length} benefit
          </span>
        </div>

        {/* At Risk stakeholders */}
        {atRisk.length > 0 && (
          <div>
            <h5 className="font-bold text-red-700 mb-3 text-sm uppercase tracking-wide">Most Vulnerable Groups</h5>
            <div className="space-y-3">
              {atRisk.map((s, idx) => <StakeholderCard key={idx} stakeholder={s} onItemClick={handleItemClick} />)}
            </div>
          </div>
        )}

        {/* Mixed impact stakeholders */}
        {mixed.length > 0 && (
          <div>
            <h5 className="font-bold text-yellow-700 mb-3 text-sm uppercase tracking-wide">Mixed Impact Groups</h5>
            <div className="space-y-3">
              {mixed.map((s, idx) => <StakeholderCard key={idx} stakeholder={s} onItemClick={handleItemClick} />)}
            </div>
          </div>
        )}

        {/* Benefiting stakeholders */}
        {benefiting.length > 0 && (
          <div>
            <h5 className="font-bold text-green-700 mb-3 text-sm uppercase tracking-wide">Benefiting Groups</h5>
            <div className="space-y-3">
              {benefiting.map((s, idx) => <StakeholderCard key={idx} stakeholder={s} onItemClick={handleItemClick} />)}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderStakeholderRisks(data) {
    return <StakeholderRisksView data={data} />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Law Analysis</h1>
        <p className="text-lg text-gray-600">
          Analyze proposed laws across multiple dimensions: values perspective, winners & losers, funding sources, and risks
        </p>
      </div>

      {/* Input Form - Only show on list view, not on detail view */}
      {!id && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <form onSubmit={handleSubmit}>
            {/* Input mode tabs */}
            <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
              {[
                { key: 'url', label: 'Paste Link' },
                { key: 'text', label: 'Paste Text' },
                { key: 'file', label: 'Upload File' }
              ].map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setInputMode(tab.key)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    inputMode === tab.key
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  disabled={loading}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* URL input */}
            {inputMode === 'url' && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Law Document URL (PDF, HTML, or DOCX)
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com/proposed-law.pdf"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {loading ? 'Analyzing...' : 'Analyze'}
                  </button>
                </div>
              </>
            )}

            {/* Text input */}
            {inputMode === 'text' && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste Document Text
                </label>
                <textarea
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  placeholder="Paste the full text of the law or bill here..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-3"
                  rows={8}
                  disabled={loading}
                />
                <div className="mb-3">
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {loading ? 'Analyzing...' : 'Analyze'}
                  </button>
                </div>
              </>
            )}

            {/* File input */}
            {inputMode === 'file' && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Document (PDF, DOCX, or TXT)
                </label>
                <div className="flex gap-2 mb-3 items-center">
                  <label
                    className={`flex-1 flex items-center justify-center px-4 py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      dragOver ? 'border-primary-500 bg-primary-50' :
                      file ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); if (!loading) setDragOver(true); }}
                    onDragEnter={(e) => { e.preventDefault(); if (!loading) setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      if (!loading && e.dataTransfer.files.length > 0) {
                        setFile(e.dataTransfer.files[0]);
                      }
                    }}
                  >
                    <div className="text-center">
                      {file ? (
                        <span className="text-sm text-primary-700 font-medium">{file.name}</span>
                      ) : (
                        <>
                          <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span className="text-sm text-gray-500">Drop a file here or click to browse</span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.docx,.doc,.txt,.html,.htm"
                      onChange={(e) => setFile(e.target.files[0] || null)}
                      className="hidden"
                      disabled={loading}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium self-start mt-4"
                  >
                    {loading ? 'Analyzing...' : 'Analyze'}
                  </button>
                </div>
              </>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="private-toggle"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                disabled={loading || !user}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="private-toggle" className={`ml-2 text-sm ${!user ? 'text-gray-400' : 'text-gray-700'}`}>
                Make this analysis private (only visible to you){!user && ' - requires login'}
              </label>
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
          </form>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="space-y-6">
          {/* Back/New Analysis Buttons during loading */}
          {id && (
            <div className="flex items-center justify-between">
              <button
                onClick={() => { navigate('/law-analysis'); setAnalysis(null); setResults([]); setLoading(false); }}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to All Laws
              </button>
              <button
                onClick={() => {
                  navigate('/law-analysis');
                  setAnalysis(null);
                  setResults([]);
                  setLoading(false);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
              >
                New Analysis
              </button>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-12">
            <div className="text-center mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">
                {analysis ? `Processing: ${analysis.title}` : id ? 'Loading analysis...' : 'Starting analysis...'}
              </p>
            </div>

          {/* Progress Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="mb-2 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">
                {analysis?.progress_message || (id ? 'Loading...' : 'Initializing...')}
              </span>
              <span className="text-sm font-semibold text-primary-600">
                {analysis?.progress || 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-primary-600 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${analysis?.progress || 0}%` }}
              ></div>
            </div>

            {/* Step-by-step indicators */}
            {analysis?.steps && Array.isArray(analysis.steps) && analysis.steps.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                {analysis.steps.map((step, idx) => {
                  const isCompleted = step.status === 'completed';
                  const isInProgress = step.status === 'in_progress';
                  const isFailed = step.status === 'failed';
                  const isSkipped = step.status === 'skipped';

                  let icon = '\u25CB'; // pending circle
                  let color = '#9e9e9e';
                  let fontWeight = 'normal';
                  let bg = 'transparent';

                  if (isCompleted) { icon = '\u2713'; color = '#2e7d32'; }
                  else if (isInProgress) { icon = '\u25CF'; color = '#1565c0'; fontWeight = 'bold'; bg = '#e3f2fd'; }
                  else if (isFailed) { icon = '\u2717'; color = '#c62828'; }
                  else if (isSkipped) { icon = '\u2014'; color = '#9e9e9e'; }

                  return (
                    <div key={step.name || idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      backgroundColor: bg,
                      marginBottom: '2px',
                      transition: 'background-color 0.3s'
                    }}>
                      <span style={{
                        color,
                        fontSize: '16px',
                        width: '20px',
                        textAlign: 'center',
                        animation: isInProgress ? 'pulse 1.5s ease-in-out infinite' : 'none'
                      }}>
                        {icon}
                      </span>
                      <span style={{
                        color: isInProgress ? '#1565c0' : isCompleted ? '#2e7d32' : isFailed ? '#c62828' : '#666',
                        fontWeight,
                        fontSize: '14px'
                      }}>
                        {step.label}
                        {isSkipped && <span style={{ fontStyle: 'italic', marginLeft: '6px', color: '#999' }}>(skipped)</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-3 text-center">
              This may take 2-5 minutes depending on document size
            </p>
          </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError('')} className="text-red-500 text-sm underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && analysis.status === 'completed' && !loading && (
        <div className="space-y-6">
          {/* Back/New Analysis Buttons - Only show on detail view */}
          {id && (
            <div className="flex items-center justify-between">
              <button
                onClick={() => { navigate('/law-analysis'); setAnalysis(null); setResults([]); setLoading(false); }}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to All Laws
              </button>
              <button
                onClick={() => {
                  navigate('/law-analysis');
                  setAnalysis(null);
                  setResults([]);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
              >
                New Analysis
              </button>
            </div>
          )}

          {/* Analysis Header */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{analysis.title}</h2>
                <div className="flex items-center flex-wrap gap-2 mt-2">
                  {analysis.scope && (
                    <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${
                      analysis.scope === 'federal' ? 'bg-blue-100 text-blue-800' :
                      analysis.scope === 'state' ? 'bg-green-100 text-green-800' :
                      analysis.scope === 'local' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {analysis.scope}
                    </span>
                  )}
                  {analysis.jurisdiction && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                      {analysis.jurisdiction}
                    </span>
                  )}
                  {analysis.state_code && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                      {analysis.state_code}
                    </span>
                  )}
                  {analysis.legislative_status && (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      analysis.legislative_status === 'Passed Legislation' ? 'bg-green-100 text-green-800' :
                      analysis.legislative_status === 'Pending Legislation' ? 'bg-yellow-100 text-yellow-800' :
                      analysis.legislative_status === 'Draft legislation' ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {analysis.legislative_status}
                    </span>
                  )}
                  <span className="text-sm text-gray-500">
                    Analyzed on {new Date(analysis.created_at).toLocaleDateString()}
                  </span>
                </div>
                <a
                  href={analysis.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-700 mt-2 inline-block"
                >
                  View Source Document →
                </a>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  analysis.status === 'completed' ? 'bg-green-100 text-green-800' :
                  analysis.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {analysis.status}
                </span>
                {user && (
                  <button
                    onClick={async () => {
                      const newPublic = !!analysis.private;
                      try {
                        const res = await fetch(`/api/law-analysis/${analysis.uuid}/visibility`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ isPublic: newPublic })
                        });
                        if (res.ok) {
                          setAnalysis(prev => ({ ...prev, private: !newPublic }));
                        }
                      } catch {}
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg font-medium ${
                      analysis.private
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                    title={analysis.private ? 'Make public to share' : 'Currently public — click to make private'}
                  >
                    {analysis.private ? 'Private' : 'Public'}
                  </button>
                )}
                {!analysis.private && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.origin + '/law-analysis/' + analysis.uuid);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="px-3 py-1.5 text-sm bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 font-medium"
                    title="Copy shareable link"
                  >
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                )}
                {user && (
                  <button
                    onClick={handleReanalyze}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    title="Re-analyze this law"
                  >
                    Re-analyze
                  </button>
                )}
                {isAdmin && (
                  <>
                    <button
                      onClick={handleEditMetadata}
                      className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      title="Edit metadata"
                    >
                      Edit Tags
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                      title="Delete this analysis"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Metadata Edit Form - Show for all logged-in users during analysis */}
            {user && (isEditingMetadata || (analysis && analysis.status === 'processing' && analysis.progress >= 15)) && (
              <form onSubmit={handleSaveMetadata} className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {analysis && analysis.status === 'processing' ? 'Review & Correct AI Metadata' : 'Edit Metadata Tags'}
                </h3>
                {analysis && analysis.status === 'processing' && (
                  <p className="text-sm text-gray-600 mb-4">
                    The AI has inferred these tags from your document. You can correct them while the analysis runs in the background.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Scope
                    </label>
                    <select
                      value={metadataForm.scope}
                      onChange={(e) => setMetadataForm({...metadataForm, scope: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select scope...</option>
                      <option value="federal">Federal</option>
                      <option value="state">State</option>
                      <option value="local">Local</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Jurisdiction
                    </label>
                    <input
                      type="text"
                      value={metadataForm.jurisdiction}
                      onChange={(e) => setMetadataForm({...metadataForm, jurisdiction: e.target.value})}
                      placeholder="e.g., United States, Indiana"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State Code (2 letters)
                    </label>
                    <input
                      type="text"
                      value={metadataForm.state_code}
                      onChange={(e) => setMetadataForm({...metadataForm, state_code: e.target.value.toUpperCase()})}
                      placeholder="e.g., IN, CA, TX"
                      maxLength={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Legislative Status
                    </label>
                    <select
                      value={metadataForm.legislative_status}
                      onChange={(e) => setMetadataForm({...metadataForm, legislative_status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select status...</option>
                      <option value="Draft legislation">Draft legislation</option>
                      <option value="Pending Legislation">Pending Legislation</option>
                      <option value="Passed Legislation">Passed Legislation</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    {analysis && analysis.status === 'processing' ? 'Update Metadata' : 'Save Metadata'}
                  </button>
                  {isEditingMetadata && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>

          {/* Summary Section */}
          {analysis.summary && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Summary</h3>
              <div className="prose prose-sm max-w-none text-gray-700">
                {analysis.summary.split('\n').map((paragraph, idx) => (
                  <p key={idx} className="mb-3 whitespace-pre-wrap">{paragraph}</p>
                ))}
              </div>
            </div>
          )}

          {/* Obligation & Process Diagrams */}
          {analysis.diagrams && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <svg className="w-6 h-6 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                Obligations & Processes
              </h3>

              {/* Obligations Diagram */}
              {analysis.diagrams.obligationsDiagram && (
                <LawDiagramRenderer diagramData={analysis.diagrams.obligationsDiagram} />
              )}

              {/* Process Diagrams */}
              {analysis.diagrams.processDiagrams && analysis.diagrams.processDiagrams.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Processes</h4>
                  {analysis.diagrams.processDiagrams.map((processDiagram, idx) => (
                    <ProcessDiagramAccordion key={idx} diagramData={processDiagram} />
                  ))}
                </div>
              )}

              {!analysis.diagrams.obligationsDiagram && (!analysis.diagrams.processDiagrams || analysis.diagrams.processDiagrams.length === 0) && (
                <p className="text-gray-500 italic">No diagrams were generated for this law.</p>
              )}
            </div>
          )}

          {/* Dimension Results */}
          {results.map((result, idx) => {
            const isError = result.textual_analysis?.startsWith('Error') || (!result.graphical_data?.length && result.textual_analysis?.length < 100);
            return (
            <div key={idx} className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className={`w-8 h-8 rounded-full ${isError ? 'bg-red-100 text-red-700' : 'bg-primary-100 text-primary-700'} flex items-center justify-center text-sm font-bold mr-3`}>
                  {isError ? '!' : idx + 1}
                </span>
                {result.dimension_name}
                {isError && <span className="ml-2 text-xs font-medium text-red-500 bg-red-50 px-2 py-0.5 rounded-full">Analysis failed</span>}
              </h3>

              {isError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-red-600 mb-3">{result.textual_analysis || 'Analysis could not be completed for this dimension.'}</p>
                  <button onClick={async () => {
                    try {
                      const r = await fetch(`/api/law-analysis/${analysis.uuid}/reanalyze`, { method: 'POST', credentials: 'include' });
                      const d = await r.json();
                      if (d.success) { setLoading(true); loadAnalysis(analysis.uuid); }
                      else alert(d.error || 'Re-analysis failed');
                    } catch (e) { alert('Error: ' + e.message); }
                  }} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700">
                    Re-analyze All Dimensions
                  </button>
                </div>
              ) : (
                <>
                  {/* Textual Analysis */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Analysis</h4>
                    <div className="prose prose-sm max-w-none text-gray-700 bg-gray-50 rounded-lg p-4">
                      {result.textual_analysis}
                    </div>
                  </div>

                  {/* Graphical Visualization */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Visualization</h4>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      {renderVisualization(result)}
                    </div>
                  </div>
                </>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Recent Analyses - Only show on main listing page, not when viewing specific analysis */}
      {!loading && !id && recentAnalyses.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              Analyzed Laws ({filteredAnalyses.length})
            </h3>
          </div>

          {/* Folder Navigation */}
          {user && (
            <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-200">
              <button
                onClick={() => setSelectedFolder(null)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedFolder === null
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({recentAnalyses.length})
              </button>
              <button
                onClick={() => setSelectedFolder('unfiled')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedFolder === 'unfiled'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Unfiled ({recentAnalyses.filter(a => !a.folder_id).length})
              </button>
              {folders.map(f => (
                <div key={f.uuid} className="relative group inline-flex items-center">
                  <button
                    onClick={() => setSelectedFolder(f.uuid)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedFolder === f.uuid
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {f.name} ({f.law_count})
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFolderModalMode('rename');
                      setFolderModalTarget(f.uuid);
                      setFolderModalName(f.name);
                      setFolderModalOpen(true);
                    }}
                    className="ml-1 text-gray-400 hover:text-gray-600 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Rename folder"
                  >
                    &#9998;
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLawFolder(f.uuid);
                    }}
                    className="ml-0.5 text-gray-400 hover:text-red-600 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete folder"
                  >
                    &#10005;
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  setFolderModalMode('create');
                  setFolderModalName('');
                  setFolderModalOpen(true);
                }}
                className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-primary-600 hover:bg-primary-50 border border-dashed border-primary-300"
              >
                + New Folder
              </button>
            </div>
          )}

          {/* Search Bar */}
          <div className="mb-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, URL, or status..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Analyses List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredAnalyses.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No analyses match your search</p>
            ) : (
              filteredAnalyses.map(item => (
                <div
                  key={item.uuid}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                    analysis?.uuid === item.uuid
                      ? 'bg-primary-50 border-2 border-primary-500'
                      : 'bg-gray-50'
                  }`}
                >
                  <button
                    onClick={() => navigate(`/law-analysis/${item.uuid}`)}
                    className="flex-1 text-left hover:opacity-80 transition-opacity"
                  >
                    <p className="font-medium text-gray-900">{item.title}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(item.created_at).toLocaleDateString()} •
                      <span className={`ml-1 ${
                        item.status === 'completed' ? 'text-green-600' :
                        item.status === 'processing' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {item.status}
                      </span>
                    </p>

                    {/* Metadata badges */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {item.folder_id && (() => {
                        const folder = folders.find(f => f.id === item.folder_id);
                        return folder ? (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                            {folder.name}
                          </span>
                        ) : null;
                      })()}
                      {item.scope && (
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                          item.scope === 'federal' ? 'bg-blue-100 text-blue-800' :
                          item.scope === 'state' ? 'bg-green-100 text-green-800' :
                          item.scope === 'local' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {item.scope}
                        </span>
                      )}
                      {item.state_code && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                          {item.state_code}
                        </span>
                      )}
                      {item.legislative_status && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          item.legislative_status === 'Passed Legislation' ? 'bg-green-100 text-green-800' :
                          item.legislative_status === 'Pending Legislation' ? 'bg-yellow-100 text-yellow-800' :
                          item.legislative_status === 'Draft legislation' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {item.legislative_status}
                        </span>
                      )}
                    </div>
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {user && folders.length > 0 && (
                      <select
                        value={item.folder_id ? (folders.find(f => f.id === item.folder_id)?.uuid || '') : ''}
                        onChange={(e) => handleMoveLawToFolder(item.uuid, e.target.value || null)}
                        className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-600 cursor-pointer"
                        title="Move to folder"
                      >
                        <option value="">Unfiled</option>
                        {folders.map(f => (
                          <option key={f.uuid} value={f.uuid}>{f.name}</option>
                        ))}
                      </select>
                    )}
                    {isAdmin && (
                      <button
                        onClick={(e) => handleDeleteFromList(item.uuid, e)}
                        className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                        title="Delete this analysis"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Folder Modal */}
      {folderModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="bg-white rounded-lg shadow-xl p-6" style={{ width: '400px' }}>
            <h3 className="text-lg font-bold mb-4">
              {folderModalMode === 'create' ? 'New Folder' : 'Rename Folder'}
            </h3>
            <input
              type="text"
              value={folderModalName}
              onChange={(e) => setFolderModalName(e.target.value)}
              placeholder="Folder name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  folderModalMode === 'create' ? handleCreateLawFolder() : handleRenameLawFolder();
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setFolderModalOpen(false); setFolderModalName(''); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={folderModalMode === 'create' ? handleCreateLawFolder : handleRenameLawFolder}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                {folderModalMode === 'create' ? 'Create' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

export default LawAnalysis;
